export type TechnicalRatingAction = 'Buy' | 'Sell' | 'Neutral'

export type TechnicalRatingLabel = 'Strong Sell' | 'Sell' | 'Neutral' | 'Buy' | 'Strong Buy'

export type TechnicalsRowResult = {
    id: string
    name: string
    value: number | undefined
    action: TechnicalRatingAction
}

export type TechnicalActionCounts = {
    buy: number
    sell: number
    neutral: number
}

export type TechnicalIndicatorLike = {
    id: string
    name: string
    value: number
    hasValue: boolean
    action: number
}

/** Proto TechnicalAction: BUY=1, SELL=2, NEUTRAL=3 */
export const toTechnicalRatingAction = (action: number): TechnicalRatingAction => {
    switch (action) {
        case 1:
            return 'Buy'
        case 2:
            return 'Sell'
        default:
            return 'Neutral'
    }
}

export const toTechnicalsRows = (items: TechnicalIndicatorLike[]): TechnicalsRowResult[] =>
    items.map((item) => ({
        id: item.id,
        name: item.name,
        value: item.hasValue ? item.value : undefined,
        action: toTechnicalRatingAction(item.action)
    }))

export const formatTechnicalsValue = (value: number | undefined): string => {
    if (value === undefined || Number.isNaN(value)) return '—'
    return value.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
}

const actionToScore = (action: TechnicalRatingAction): number => {
    switch (action) {
        case 'Buy':
            return 1
        case 'Sell':
            return -1
        default:
            return 0
    }
}

export const countActions = (rows: TechnicalsRowResult[]): TechnicalActionCounts =>
    rows.reduce(
        (counts, row) => {
            switch (row.action) {
                case 'Buy':
                    counts.buy += 1
                    break
                case 'Sell':
                    counts.sell += 1
                    break
                default:
                    counts.neutral += 1
            }
            return counts
        },
        { buy: 0, sell: 0, neutral: 0 }
    )

export const mergeActionCounts = (...groups: TechnicalActionCounts[]): TechnicalActionCounts =>
    groups.reduce(
        (merged, group) => ({
            buy: merged.buy + group.buy,
            sell: merged.sell + group.sell,
            neutral: merged.neutral + group.neutral
        }),
        { buy: 0, sell: 0, neutral: 0 }
    )

export const scoreFromActions = (rows: TechnicalsRowResult[]): number => {
    if (rows.length === 0) return 0
    const total = rows.reduce((sum, row) => sum + actionToScore(row.action), 0)
    return total / rows.length
}

export const mapScoreToLabel = (score: number): TechnicalRatingLabel => {
    if (score < -0.5) return 'Strong Sell'
    if (score < -0.1) return 'Sell'
    if (score <= 0.1) return 'Neutral'
    if (score <= 0.5) return 'Buy'
    return 'Strong Buy'
}

export const isBullishLabel = (label: TechnicalRatingLabel): boolean => label === 'Buy' || label === 'Strong Buy'

export const isBearishLabel = (label: TechnicalRatingLabel): boolean => label === 'Sell' || label === 'Strong Sell'
